import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash01, XClose } from '@untitledui/icons';
import { memo, useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { DraggableCard } from '@/lib/base/card';
import { cx } from '@/utils/cx';

import type { CardRow } from '../utils/cardConfig';
import type {
  CardFieldRef,
  FieldPickerOption,
} from '../utils/cardConfigOptions';
import { CardFieldPicker } from './CardFieldPicker';

// ── Module-level constants ───────────────────────────────────────────────────

/** Recessed fallback section — visually subordinate to the primary field. */
const FALLBACK_SECTION_CLASS = cx(
  'rounded-md bg-bg-secondary px-2.5 py-2 mt-1.5',
  'flex flex-col gap-1.5'
);

// ── Sub-component props ──────────────────────────────────────────────────────

interface CardFieldFallbackProps {
  rowIndex: number;
  slotIndex: number;
  fallbackIndex: number;
  fb: CardRow['slots'][number];
  properties: FieldPickerOption[];
  associations: FieldPickerOption[];
  disabledKeys: Set<string>;
  onSetFallbackField(
    rowIndex: number,
    slotIndex: number,
    fallbackIndex: number,
    field: CardFieldRef
  ): void;
  onRemoveFallback(
    rowIndex: number,
    slotIndex: number,
    fallbackIndex: number
  ): void;
}

interface CardFieldSlotProps {
  rowIndex: number;
  slotIndex: number;
  slot: CardRow['slots'][number];
  properties: FieldPickerOption[];
  associations: FieldPickerOption[];
  allChosenKeys: Set<string>;
  onSetSlotField(
    rowIndex: number,
    slotIndex: number,
    field: CardFieldRef
  ): void;
  onRemoveSlot(rowIndex: number, slotIndex: number): void;
  onAddFallback(rowIndex: number, slotIndex: number): void;
  onSetFallbackField(
    rowIndex: number,
    slotIndex: number,
    fallbackIndex: number,
    field: CardFieldRef
  ): void;
  onRemoveFallback(
    rowIndex: number,
    slotIndex: number,
    fallbackIndex: number
  ): void;
  onToggleSlotLabel(rowIndex: number, slotIndex: number): void;
}

// ── Public props ─────────────────────────────────────────────────────────────

export interface CardFieldRowProps {
  id: string;
  row: CardRow;
  rowIndex: number;
  properties: FieldPickerOption[];
  associations: FieldPickerOption[];
  onSetSlotField(
    rowIndex: number,
    slotIndex: number,
    field: CardFieldRef
  ): void;
  onAddSlot(rowIndex: number): void;
  onRemoveRow(rowIndex: number): void;
  onRemoveSlot(rowIndex: number, slotIndex: number): void;
  onAddFallback(rowIndex: number, slotIndex: number): void;
  onSetFallbackField(
    rowIndex: number,
    slotIndex: number,
    fallbackIndex: number,
    field: CardFieldRef
  ): void;
  onRemoveFallback(
    rowIndex: number,
    slotIndex: number,
    fallbackIndex: number
  ): void;
  onToggleSlotLabel(rowIndex: number, slotIndex: number): void;
}

// ── CardFieldFallback ────────────────────────────────────────────────────────

const CardFieldFallback = memo(function CardFieldFallback({
  rowIndex,
  slotIndex,
  fallbackIndex,
  fb,
  properties,
  associations,
  disabledKeys,
  onSetFallbackField,
  onRemoveFallback,
}: CardFieldFallbackProps) {
  const handleFbSelect = useCallback(
    (field: CardFieldRef) =>
      onSetFallbackField(rowIndex, slotIndex, fallbackIndex, field),
    [rowIndex, slotIndex, fallbackIndex, onSetFallbackField]
  );

  const handleRemoveFb = useCallback(
    () => onRemoveFallback(rowIndex, slotIndex, fallbackIndex),
    [rowIndex, slotIndex, fallbackIndex, onRemoveFallback]
  );

  return (
    <div className='flex items-center gap-1.5'>
      <span className='text-xs text-[var(--color-text-tertiary)] select-none shrink-0'>
        or
      </span>
      <div className='flex-1 min-w-0'>
        <CardFieldPicker
          properties={properties}
          associations={associations}
          value={fb.name ? { type: fb.type, name: fb.name } : undefined}
          disabledKeys={disabledKeys}
          onSelect={handleFbSelect}
          triggerAriaLabel={`Fallback ${fallbackIndex + 1} for field ${slotIndex + 1} in row ${rowIndex + 1}`}
        />
      </div>
      <Button
        color='tertiary'
        size='xs'
        iconLeading={XClose}
        onClick={handleRemoveFb}
        aria-label={`Remove fallback ${fallbackIndex + 1} from field ${slotIndex + 1}`}
      />
    </div>
  );
});

// ── CardFieldSlot ────────────────────────────────────────────────────────────

/**
 * A single slot within a row.
 *
 * Two visual zones create hierarchy:
 * - **Primary picker** — top, normal card background, prominent
 * - **Fallback section** — below, `bg-secondary` inset, subordinate
 */
const CardFieldSlot = memo(function CardFieldSlot({
  rowIndex,
  slotIndex,
  slot,
  properties,
  associations,
  allChosenKeys,
  onSetSlotField,
  onRemoveSlot,
  onAddFallback,
  onSetFallbackField,
  onRemoveFallback,
  onToggleSlotLabel,
}: CardFieldSlotProps) {
  const handleSlotSelect = useCallback(
    (field: CardFieldRef) => onSetSlotField(rowIndex, slotIndex, field),
    [rowIndex, slotIndex, onSetSlotField]
  );

  const handleRemoveSlot = useCallback(
    () => onRemoveSlot(rowIndex, slotIndex),
    [rowIndex, slotIndex, onRemoveSlot]
  );

  const handleAddFallback = useCallback(
    () => onAddFallback(rowIndex, slotIndex),
    [rowIndex, slotIndex, onAddFallback]
  );

  const handleToggleLabel = useCallback(
    () => onToggleSlotLabel(rowIndex, slotIndex),
    [rowIndex, slotIndex, onToggleSlotLabel]
  );

  const disabledKeys = useMemo(() => {
    const s = new Set(allChosenKeys);
    if (slot.name) {
      s.delete(`${slot.type}:${slot.name}`);
    }
    return s;
  }, [allChosenKeys, slot.type, slot.name]);

  const fallbackDisabledSets = useMemo(
    () =>
      (slot.fallbacks ?? []).map(fb => {
        const s = new Set(allChosenKeys);
        if (fb.name) {
          s.delete(`${fb.type}:${fb.name}`);
        }
        return s;
      }),
    [slot.fallbacks, allChosenKeys]
  );

  const fallbacks = slot.fallbacks ?? [];

  return (
    <div className='flex flex-col flex-1 min-w-0'>
      {/* ── Primary field picker ─────────────────────────────────── */}
      <div className='flex items-center gap-1.5'>
        <div className='flex-1 min-w-0'>
          <CardFieldPicker
            properties={properties}
            associations={associations}
            value={slot.name ? { type: slot.type, name: slot.name } : undefined}
            disabledKeys={disabledKeys}
            onSelect={handleSlotSelect}
            triggerAriaLabel={`Field ${slotIndex + 1} for row ${rowIndex + 1}`}
          />
        </div>

        <Button
          color='tertiary'
          size='xs'
          iconLeading={XClose}
          onClick={handleRemoveSlot}
          aria-label={`Remove field ${slotIndex + 1}`}
        />
      </div>

      {/* ── Show label toggle ────────────────────────────────── */}
      {slot.name && (
        <label className='flex items-center gap-1.5 mt-1 cursor-pointer select-none'>
          <input
            type='checkbox'
            checked={!!slot.showLabel}
            onChange={handleToggleLabel}
            className='accent-[var(--color-brand-primary)] size-3.5'
            aria-label={`Show label for field ${slotIndex + 1}`}
          />
          <span className='text-xs text-[var(--color-text-tertiary)]'>
            Show label
          </span>
        </label>
      )}

      {/* ── Fallback section — recessed bg, visually subordinate ── */}
      {fallbacks.length > 0 && (
        <div className={FALLBACK_SECTION_CLASS}>
          {fallbacks.map((fb, fallbackIndex) => (
            <CardFieldFallback
              // Fallbacks lack stable IDs; position within a slot is the identity
              // eslint-disable-next-line react/no-array-index-key
              key={`fb:${slotIndex}:${fallbackIndex}`}
              rowIndex={rowIndex}
              slotIndex={slotIndex}
              fallbackIndex={fallbackIndex}
              fb={fb}
              properties={properties}
              associations={associations}
              disabledKeys={
                fallbackDisabledSets[fallbackIndex] ?? new Set<string>()
              }
              onSetFallbackField={onSetFallbackField}
              onRemoveFallback={onRemoveFallback}
            />
          ))}
          <Button
            color='link-gray'
            size='xs'
            iconLeading={Plus}
            onClick={handleAddFallback}
            aria-label={`Add fallback to field ${slotIndex + 1}`}
          >
            or
          </Button>
        </div>
      )}

      {/* "Add or" discovery button when no fallbacks yet */}
      {fallbacks.length === 0 && (
        <Button
          color='link-gray'
          size='xs'
          iconLeading={Plus}
          onClick={handleAddFallback}
          className='mt-1'
          aria-label={`Add fallback to field ${slotIndex + 1}`}
        >
          or
        </Button>
      )}
    </div>
  );
});

// ── CardFieldRow ─────────────────────────────────────────────────────────────

/**
 * One row in the card-fields configuration list, wrapped in a DraggableCard.
 *
 * ```
 * ┌──┬──────────────────────────────────────────┐
 * │  │  [Status ▾]              [+slot]  [×row] │  ← row contents
 * │⠿│  ╭ bg-secondary ───────────────────────╮ │
 * │  │  │ or [Amount ▾] [×]   + or            │ │  ← element logic
 * │  │  ╰─────────────────────────────────────╯ │
 * └──┴──────────────────────────────────────────┘
 * ```
 */
export function CardFieldRow({
  id,
  row,
  rowIndex,
  properties,
  associations,
  onSetSlotField,
  onAddSlot,
  onRemoveRow,
  onRemoveSlot,
  onAddFallback,
  onSetFallbackField,
  onRemoveFallback,
  onToggleSlotLabel,
}: CardFieldRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = useMemo(
    () => ({
      transform: CSS.Translate.toString(transform),
      transition,
    }),
    [transform, transition]
  );

  const dragHandleProps = useMemo(
    () => ({ ...listeners, ...attributes }),
    [listeners, attributes]
  );

  // Dedupe keys are `${type}:${name}`, not bare names — a property and an
  // association can legitimately share a name, and keying off name alone would
  // wrongly block selecting one once the other is chosen.
  const allChosenKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const slot of row.slots) {
      if (slot.name) {
        keys.add(`${slot.type}:${slot.name}`);
      }
      for (const fb of slot.fallbacks ?? []) {
        if (fb.name) {
          keys.add(`${fb.type}:${fb.name}`);
        }
      }
    }
    return keys;
  }, [row.slots]);

  const handleAddSlotLocal = useCallback(
    () => onAddSlot(rowIndex),
    [rowIndex, onAddSlot]
  );

  const handleRemoveRowLocal = useCallback(
    () => onRemoveRow(rowIndex),
    [rowIndex, onRemoveRow]
  );

  const canAddSlot = row.slots.length < 2;

  return (
    <DraggableCard
      nodeRef={setNodeRef}
      style={style}
      isDragging={isDragging}
      dragHandleProps={dragHandleProps}
      dragHandleLabel='Drag to reorder – press Space to pick up, arrow keys to move, Space or Escape to drop'
      handleClassName='hidden md:flex'
      className='group/row'
    >
      <div className='flex items-start gap-2 p-2.5'>
        {/* Slot columns — stack vertically on mobile, side-by-side on md+ */}
        <div className='flex-1 min-w-0 flex flex-col md:flex-row md:items-start gap-2 md:gap-3'>
          {row.slots.map((slot, slotIndex) => (
            <div
              // Slots lack stable IDs; position within a row is the identity
              // eslint-disable-next-line react/no-array-index-key
              key={`slot:${slotIndex}`}
              className='flex-1 min-w-0'
            >
              {row.slots.length > 1 && (
                <span className='md:hidden block text-xs font-medium text-[var(--color-text-tertiary)] mb-1'>
                  Column {slotIndex + 1}
                </span>
              )}
              <CardFieldSlot
                rowIndex={rowIndex}
                slotIndex={slotIndex}
                slot={slot}
                properties={properties}
                associations={associations}
                allChosenKeys={allChosenKeys}
                onSetSlotField={onSetSlotField}
                onRemoveSlot={onRemoveSlot}
                onAddFallback={onAddFallback}
                onSetFallbackField={onSetFallbackField}
                onRemoveFallback={onRemoveFallback}
                onToggleSlotLabel={onToggleSlotLabel}
              />
            </div>
          ))}
        </div>

        {/* Row-level controls — add column + remove row */}
        <div className='flex items-center gap-0.5 shrink-0 mt-1'>
          {canAddSlot && (
            <Button
              color='tertiary'
              size='xs'
              iconLeading={Plus}
              onClick={handleAddSlotLocal}
              aria-label='Add second field'
            />
          )}
          <Button
            color='tertiary'
            size='xs'
            iconLeading={Trash01}
            onClick={handleRemoveRowLocal}
            className='md:opacity-0 md:group-hover/row:opacity-100 transition-opacity duration-100'
            aria-label='Remove row'
          />
        </div>
      </div>
    </DraggableCard>
  );
}
